import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { SelectComponent } from '@app/shared/components/form/select/select.component';
import { ToastService } from '@teorema/patron';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { AccessType } from 'src/app/models/bw-user.model';
import { System } from 'src/app/models/core-config.model';
import { User } from 'src/app/models/user.model';
import { utils } from 'src/app/shared';
import { AuthService } from '../auth.service';

@Component({
  selector: 'patron-select-company',
  templateUrl: './select-company.component.html',
  standalone: false,
})
export class SelectCompanyComponent implements OnInit, OnDestroy {
  authForm: FormGroup;
  companyGroups: any[] = [];
  loading: boolean = false;
  isSupportOptionsLoaded: boolean = false;
  loadingSupportOptions: boolean = false;
  supportProfiles: any[] = [];
  user: User;
  patronUtils = utils;

  @Input() submitButtonText: string = 'Entrar';
  @Output() onSubmit: EventEmitter<any> = new EventEmitter();
  @ViewChild('cgSelect') cgSelect?: SelectComponent;

  private searchTerm = new Subject<string>();
  private destroy$ = new Subject<void>();
  private currentSearchRequest: Promise<any> | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private toastService: ToastService
  ) {
    this.user = utils.getUser();
    this.authForm = this.formBuilder.group({
      company_group: [null],
    });

    this.companyGroups = this.markBlockedCompanyGroups(
      this.enrichCompanyGroupsWithCustomerName(utils.getAvailableAccesses())
    );
    if (utils.getSystem() != System.AUDITOR) {
      if (this.user.enable_support) this.loadSupportOptions();
    }
  }

  /**
   * Traduz o `blocked` que veio do login para o `disabled` que o ng-select lê.
   *
   * A empresa com cadastro irregular continua na lista de propósito: sumir sem
   * explicação é o que faz o cliente ligar perguntando "cadê a minha empresa?".
   * Desabilitada e com o motivo ao lado, ele já liga sabendo o que pedir.
   *
   * Só `blocked` desabilita. `irregular` sozinho é diagnóstico — é o que o
   * suporte vê, inclusive com o bloqueio desligado, e ele precisa poder entrar
   * justamente no cliente irregular para regularizá-lo.
   */
  private markBlockedCompanyGroups(companyGroups: any[]): any[] {
    return companyGroups.map(cg => (cg?.blocked ? { ...cg, disabled: true } : cg));
  }

  private enrichCompanyGroupsWithCustomerName(companyGroups: any[]): any[] {
    if (!this.user?.bw_user_company_groups?.length) {
      return companyGroups;
    }

    return companyGroups.map(cg => {
      if (!cg.pivot?.customer_id) {
        return cg;
      }

      // Encontra o bw_user_company_group correspondente
      const bwUserCg = this.user?.bw_user_company_groups?.find(
        (bwcg: any) => bwcg.customer_id === cg.pivot.customer_id
      );

      if (!bwUserCg) {
        return cg;
      }

      // Se encontrou e tem customer_name, adiciona ao nome/alias exibido apenas se for tipo Cliente
      const name = bwUserCg?.customer?.person?.name;
      const isCustomerAccess = cg.access_type?.key === AccessType.Customer;

      if (name && isCustomerAccess) {
        return {
          ...cg,
          displayName: `${cg.alias || cg.name} - ${name}`,
        };
      }

      return {
        ...cg,
        displayName: cg.alias || cg.name,
      };
    });
  }

  ngOnInit(): void {
    this.searchTerm.pipe(debounceTime(400), takeUntil(this.destroy$)).subscribe(term => {
      this.loadSupportOptions(term);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadSupportOptions(q?: string) {
    // Cancelar requisição anterior se ainda estiver pendente
    if (this.currentSearchRequest && this.loadingSupportOptions) {
      // A requisição anterior ainda está em andamento, não faremos nada
      return;
    }

    this.loadingSupportOptions = true;

    // Armazenar referência da requisição atual
    this.currentSearchRequest = this.authService
      .supportCompanyGroups(q)
      .then((response: any) => {
        // Verificar se esta ainda é a requisição atual
        if (this.currentSearchRequest === null) {
          return; // Requisição foi cancelada
        }

        const company_groups = response?.data?.company_groups;
        if (company_groups?.length) {
          company_groups.forEach((cg: any) => {
            if (
              !this.companyGroups.find(
                i => i.id == cg.id && i.access_type.key == AccessType.Support
              )
            )
              this.companyGroups.push({
                ...cg,
                access_type: { key: AccessType.Support, label: 'Suporte' },
              });
          });
          this.companyGroups = [...this.companyGroups];
          this.isSupportOptionsLoaded = true;
        }

        const bw_user_profiles = response?.data?.bw_user_profiles;
        if (bw_user_profiles?.length) {
          this.supportProfiles = bw_user_profiles;
        }
      })
      .catch(err => {
        // Verificar se esta ainda é a requisição atual
        if (this.currentSearchRequest === null) {
          return; // Requisição foi cancelada
        }

        if (!err?.messageShown)
          this.toastService.show({
            color: 'error',
            message: 'Houve um erro ao carregar as opções de suporte',
          });
      })
      .finally(() => {
        // Limpar apenas se esta ainda é a requisição atual
        if (this.currentSearchRequest !== null) {
          this.loadingSupportOptions = false;
          this.currentSearchRequest = null;
        }
      });

    await this.currentSearchRequest;
  }

  changeCompanyGroup() {
    const cg = this.authForm.get('company_group')?.value;
    if (!cg) return;
    if (!cg.access_type) return;
    utils.setAccessType(cg.access_type.key);

    if (cg.id) {
      utils.setCompanyGroup(cg);
      if (this.currentAccessType == AccessType.Support) {
        this.user.bw_user_profiles = this.supportProfiles.filter(i => i.company_group_id == cg.id);
        utils.setUser(this.user);
      }
    }

    this.onSubmit.emit();
  }

  get currentCompanyGroup() {
    return utils.getCompanyGroup();
  }

  get currentAccessType() {
    return utils.getAccessType();
  }

  selectSearchChanged(event: any) {
    if (!event?.term) return;
    if (!this.user.enable_support) return;

    this.searchTerm.next(event.term);
  }
}
